import { LocalDate, LocalTime } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyle } from "trader.ts/ui";
import { Parsing } from "trader.ts/ui.forms";
import { checkAllCasesHandled, tryExtractErrorMessage } from "trader.ts/utils";
import { match, P } from "ts-pattern";
import {
  enrollment_status,
  enrollmentid,
  enrollmentrequestid,
  expectedpaymentid,
  instrumentid,
  internalteacherid,
  lesson_location,
  lessongroupid,
  roomid,
  studentid,
  teacherid,
  weekday,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { renderErrorPanel } from "../../client/savebutton";
import { invoiceFull } from "../../domain/finance/types";
import * as routes from "../../routes";
import {
  renderExpectedPaymentRows,
  renderExpectedPaymentsRedirectButton,
} from "../student_financial/client";
import currency from "currency.js";

type enrollment = {
  student: {
    firstname: string;
    lastname: string;
    id: studentid;
  };
  id: enrollmentid;
  status: enrollment_status;
  lessongroup: {
    id: lessongroupid;
    firstlesson: LocalDate | null;
    lastlesson: LocalDate | null;
    time: LocalTime;
    weekday: weekday;
    roomid: roomid | null;
    location: lesson_location;
  };
  instrument: {
    description: string;
    id: instrumentid;
  };
  teacher: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  room_description: string | null;
};

type payment_target =
  | {
      enrollmentrequest: enrollmentrequestid;
    }
  | {
      expectedpayments: {
        expectedpaymentids: expectedpaymentid[];
      };
    };

export function render(
  opts:
    | {
        tag: "select_payments";
        enrollment: enrollment;
        invoice: invoiceFull | null;
        periodfeeInvoice: invoiceFull | null;
      }
    | {
        tag: "start_payment_select_methods";
        amountInEur: currency;
        target: payment_target;
      },
): HTMLElement | HTMLElement[] {
  if (opts.tag === "select_payments") {
    return renderSelectPaymentsComponent(opts);
  } else if (opts.tag === "start_payment_select_methods") {
    return renderPaymentMethodSelection(opts);
  } else {
    return checkAllCasesHandled(opts);
  }
}

function renderPaymentMethodSelection(opts: {
  tag: "start_payment_select_methods";
  amountInEur: currency;
  target: payment_target;
}) {
  const paymentMethodsRemoteS: Source<
    Parsing<GetReturnTypeFromApiSpec<typeof routes.getMolliePaymentMethodsSpec>>
  > = new Source({ tag: "initial" });

  function fetchPaymentMethods() {
    paymentMethodsRemoteS.set({ tag: "loading" });
    rpc(routes.getMolliePaymentMethodsSpec, undefined, {
      amountInEur: opts.amountInEur,
    })
      .then((res) => paymentMethodsRemoteS.set({ tag: "parsed", parsed: res }))
      .catch((err: unknown) =>
        paymentMethodsRemoteS.set({
          tag: "err",
          label: tryExtractErrorMessage(err),
        }),
      );
  }

  return dyn(paymentMethodsRemoteS, (remote) => {
    return match(remote)
      .with({ tag: "initial" }, () => (
        <a className={style.a_around_button} onclick={fetchPaymentMethods}>
          <button className={style.button_accent}>
            {"Start je betaling!"}
          </button>
        </a>
      ))
      .with({ tag: "loading" }, () => (
        <a className={style.a_around_button}>
          <button className={style.button_accent + style.loading}></button>
        </a>
      ))
      .with({ tag: "err" }, (error) => (
        <div>
          {renderErrorPanel({ error: error.label || "" })}
          <div style="text-align: right;">
            <button
              className={style.button_accent}
              onclick={fetchPaymentMethods}
            >
              {"Probeer opnieuw"}
            </button>
          </div>
        </div>
      ))
      .with({ tag: "parsed" }, (p) => {
        const selectedS: Source<null | (typeof p)["parsed"][number]> =
          new Source(null);
        const st = `
        display: flex;
        justify-content: flex-start;
        align-items: center;
        background-color: var(--grey-100);
        height: 80px;
        padding: 0px 8px;
        cursor: pointer;
`;
        const panels = p.parsed.map((m) =>
          dynStyle(
            selectedS,
            (sel) =>
              sel === null
                ? ""
                : sel.id === m.id
                ? "background-color: white; outline: 2px solid var(--accent-500);"
                : "",
            <div
              className={style.panel}
              style={st}
              onclick={() => selectedS.set(m)}
            >
              <img
                alt={m.description}
                src={m.image.svg}
                style="height: calc(100% - 16px); margin-right: 16px;"
              />
              <div>
                <b>{m.description}</b>
              </div>
            </div>,
          ),
        );
        const label = "Start je betaling!";
        return (
          <div style="text-align: left;">
            <label for="">{"Kies je betaalmethode"}</label>
            <section>{panels}</section>
            <article style="text-align: right;">
              {dyn(selectedS, (sel) =>
                match(sel)
                  .with(null, () => (
                    <button className={style.button_accent} disabled="disabled">
                      {label}
                    </button>
                  ))
                  .with(P._, (method) => (
                    <a
                      className={style.a_around_button}
                      href={routes.startPaymentFlowInvoiceRoute.link({
                        target: opts.target,
                        method: method.id,
                      })}
                    >
                      <button className={style.button_accent}>{label}</button>
                    </a>
                  ))
                  .exhaustive(),
              )}
            </article>
          </div>
        );
      })
      .exhaustive();
  });
}

function renderSelectPaymentsComponent(opts: {
  tag: "select_payments";
  enrollment: enrollment;
  invoice: invoiceFull | null;
  periodfeeInvoice: invoiceFull | null;
}) {
  const today = LocalDate.now();
  const allExpectedPayments = (opts.invoice?.expectedpayments || []).concat(
    opts.periodfeeInvoice?.expectedpayments || [],
  );
  const selectedPaymentsS = new Source(
    allExpectedPayments
      .filter(
        (ep) =>
          (ep.amount_left.value > 0 && ep.periodic_payment_method === null) ||
          (ep.periodic_payment_method !== null &&
            ep.date.isAfter(today.minusDays(7))),
      )
      .map((ep) => ({
        id: ep.id,
        amount_left: ep.amount_left,
      })),
  );

  return (
    <div>
      <h2>{"Welke betalingen wil je uitvoeren?"}</h2>
      {renderExpectedPaymentRows(opts, true, selectedPaymentsS)}

      <div style="text-align: right">
        {dyn(selectedPaymentsS, (selectedPayments) => {
          return renderExpectedPaymentsRedirectButton(selectedPayments);
        })}
      </div>
    </div>
  );
}
